import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { FormikHelpers, FormikProps } from 'formik';
import { FormGroup, Grid } from '@material-ui/core';
import Banner from '@cpg-its/ads.containers.banner';
import { ButtonWrapper } from '../../../global/components/layout/button-wrapper';
import { DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmEpiApiState, TcsmStates } from '../../../root/tcsm-model';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { getTierLabel } from '../helpers/annual-enrollment-helpers';
import { AESteps, AnnualEnrollmentState } from '../models/annual-enrollment-models';
import { AnnualEnrollmentForm } from './annual-enrollment-form';
import { TcsmModal } from '../../../global/components/tcsm-modal';
import { AnnualEnrollmentFormValues } from '../models/annual-enrollment-form-models';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { verifyAndSubmitAnnualEnrollment } from '../helpers/annual-enrollment-requests';
import { useEffectUpdate } from '../../../global/hooks/useEffectUpdate';
import { setAEStep } from '../annual-enrollment-slice';
import { gridPageStyles } from '../../../global/constants/theme/styles/layout/grid-page-layout';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { selectedReviewItemStyles } from '../../../global/constants/theme/styles/components/selected-review-item';
import { getFormattedDate } from '../../../global/helpers/date-helpers';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';

interface Props {
	basePath?: string;
}

export function AnnualEnrollmentCoverage(props: Props) {
	const app = useContext(DemographicsContext);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const AESettings = epiProps?.annualEnrollmentSettingsState?.epiContent;
	const labelSettings = epiProps?.labelSettingState?.epiContent;
	const AEState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const history = useHistory();
	const [previousModalOpen, setPreviousModalOpen] = useState(false);
	const [processing, setProcessing] = useState(false);
	const pageRef = useRef<HTMLDivElement>(null);
	const medicalEnrollment = AEState.AEInfo?.enrollments_base?.M[0];
	const dentalEnrollment = AEState.AEInfo?.enrollments_base?.D[0];
	const pageClasses = gridPageStyles();
	const dispatch = useDispatch();
	const selectedItemClasses = selectedReviewItemStyles();
	const groupId = useSelector<TcsmStates, number>(state => state?.groupsState?.currentGroup?.group_id);

	const buttonClasses = buttonStyles();
	
	const showReview = AEState?.currentStep === AESteps.Review;

	//scroll to top whenever the form step changes
	const showReviewCallback = useCallback(() => window.scrollTo({
		top: 0,
		left: 0,
		behavior: 'smooth',
	}), [showReview]);
	useEffectUpdate(showReviewCallback);


	useEffect(() => {
		dispatch(setAEStep(showReview ? AESteps.Review : AESteps.Coverage, app));
	}, [showReview]);

	const handleClose = () => {
		setPreviousModalOpen(false);
	};

	const medicalExpired = AEState?.AEInfo?.offerings?.M?.plans?.filter(p => p.code === AEState?.AEInfo?.enrollments_base.M[0]?.code)?.length === 0;
	const dentalExpired = AEState?.AEInfo?.offerings?.D?.plans?.filter(p => p.code === AEState?.AEInfo?.enrollments_base.D[0]?.code)?.length === 0;

	const coveragePrevStep = `${props.basePath}${AccountInfoRoutes.RelationshipsSegment}`;

	const prevStepOnClick = () => {
		history.push(coveragePrevStep);
	};

	const previousStepAction = (formikProps: FormikProps<AnnualEnrollmentFormValues>) => {
		if (showReview) {
			formikProps.setTouched({});
			formikProps.setFieldValue('agreement', false);
			dispatch(setAEStep(AESteps.Coverage, app));
		} else {
			setPreviousModalOpen(true);
		}
	};

	const onSubmit = (values: AnnualEnrollmentFormValues, savedValues: AnnualEnrollmentFormValues, formikHelpers: FormikHelpers<AnnualEnrollmentFormValues>) => {
		if (!showReview) {
			//setShowReview(true);
			dispatch(setAEStep(AESteps.Review, app));
			formikHelpers.setTouched({});
			formikHelpers.setSubmitting(false);

			return;
		}

		const callBack = () => {
			dispatch(setAEStep(AESteps.Coverage, app));
			formikHelpers.setFieldValue('agreement', false);
			setProcessing(false);
		};
		setProcessing(true);
		verifyAndSubmitAnnualEnrollment(dispatch, app, personGuid, values, AEState, callBack, groupId);
	};

	const CoverageDisplay = () => {
		if (showReview) return null;

		return (
			<DividerSection sectionText={AESettings.currentPlanLabel}>

				{
					// current medical plan is no longer offered
					medicalEnrollment && medicalExpired &&
					<ContentSpacer>
						<Banner banner={{
							type: 'warning',
							label: <p className='h5-styled'>{AESettings?.medicalNoLongerOffered?.replace(/{date}/gi, getFormattedDate(AEState?.AEInfo?.ae_status?.effective_date))}</p>,
						}} />
					</ContentSpacer>
				}
				{
					// current dental plan is no longer offered
					dentalEnrollment && dentalExpired &&
					<ContentSpacer>
						<Banner banner={{
							type: 'warning',
							label: <p className='h5-styled'>{AESettings?.dentalNoLongerOffered?.replace(/{date}/gi, getFormattedDate(AEState?.AEInfo?.ae_status?.effective_date))}</p>,
						}} />
					</ContentSpacer>
				}
				<FormGroup>
					{
						medicalEnrollment &&
						<div className={`divider-section-item ${selectedItemClasses.selectedReviewItem}`}>
							<p><strong>{AESettings.medicalPlanLabel}</strong></p>
							<div className='selected-review-item-inner mobile-stack'>

								<div className='selected-review-item-content'>
									<div>

										<p>{medicalEnrollment?.name}</p>
										<p className='input-caption'>{getTierLabel(medicalEnrollment?.tier, AESettings)}</p>
									</div>
								</div>
								{medicalExpired && <p className='selected-review-item-subtext red-text'><strong>{AESettings?.noLongerOfferedText}</strong></p>}
							</div>
						</div>
					}
					{
						dentalEnrollment &&
						<div className={`divider-section-item ${selectedItemClasses.selectedReviewItem}`}>
							<h6>{AESettings.dentalPlanLabel}</h6>
							<div className='selected-review-item-inner mobile-stack'>

								<div className='selected-review-item-content'>
									<div>
										<p>{dentalEnrollment?.name}</p>
										<p className='input-caption'>{getTierLabel(dentalEnrollment?.tier, AESettings)}</p>
									</div>
								</div>
								{dentalExpired && <p className='selected-review-item-subtext red-text'><strong>{AESettings?.noLongerOfferedText}</strong></p>}
							</div>
						</div>
					}
				</FormGroup>
			</DividerSection>
		);
	};

	return (
		<div className={pageClasses.gridPageContainer} ref={pageRef}>


			<AnnualEnrollmentForm
				annualEnrollmentProps={AESettings}
				labelSettings={labelSettings}
				medicalExpired={medicalExpired}
				dentalExpired={dentalExpired}
				showReview={showReview}
				previousStepAction={previousStepAction}
				onSubmit={onSubmit}
				coverageDisplay={<CoverageDisplay />}
			/>
			{
				!processing &&
				<div className='grid-main-content'>
					<Grid container direction="row" justifyContent="flex-end">
						<Grid
							justifyContent="flex-end"
							container
							item md={4}>

							<div dangerouslySetInnerHTML={{ __html: showReview ? AESettings.submitHelperText : AESettings.continueHelperText }} />
						</Grid>
					</Grid>
				</div>
			}
			<TcsmModal
				open={previousModalOpen}
				handleClose={handleClose}>
				<>
					<h2>{AESettings.previousStepModalTitle}</h2>
					<div dangerouslySetInnerHTML={{ __html: AESettings.previousStepModalDescription }} />
					<ButtonWrapper flexEnd>
						<button type='button' className={buttonClasses.tertiaryButton} onClick={handleClose}>{AESettings.previousStepModalCancel}</button>
						<button type='button' className={buttonClasses.primaryButton} onClick={prevStepOnClick} >{AESettings.previousStepModalConfirm}</button>						
					</ButtonWrapper>
				</>

			</TcsmModal>
		</div>
	);
}

AnnualEnrollmentCoverage.defaultProps = {
	basePath: '',
};